import { useEffect, useState } from "react";
import { hieraApi } from "../lib/api";
import { t, type Lang } from "../lib/i18n";

type Person = {
  id: string;
  heronSub?: string | null;
  displayName: string;
  username?: string | null;
  online: boolean;
  lastSeen: string | null;
  email?: string;
  role?: string;
  avatarUrl?: string | null;
  avatarEmoji?: string;
};

type Props = { lang: Lang; staff?: boolean };

function seen(iso: string | null, lang: Lang): string {
  if (!iso) return t(lang, "offline");
  return new Date(iso).toLocaleString(lang === "ru" ? "ru-RU" : "en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export function PeoplePage({ lang, staff }: Props) {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void hieraApi
      .people()
      .then((res) => {
        if (!cancelled) {
          setErr(null);
          setPeople(res.people);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setErr(t(lang, "peopleLoadFail"));
          setPeople([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  return (
    <div className="account pad">
      <h1>{staff ? t(lang, "admin") : t(lang, "members")}</h1>
      {!people ? <p className="muted">…</p> : null}
      {err ? <p className="muted">{err}</p> : null}
      {people && !people.length && !err ? <p className="muted">{t(lang, "peopleEmpty")}</p> : null}
      <ul className="people-list">
        {(people || []).map((person) => {
          const nick = person.username?.trim();
          return (
            <li key={person.id}>
              <span className={person.online ? "presence on" : "presence"} title={person.online ? t(lang, "online") : t(lang, "offline")} />
              {person.avatarUrl ? (
                <img className="people-avatar" src={person.avatarUrl} alt="" width={36} height={36} />
              ) : (
                <span className="people-avatar people-avatar--fallback" aria-hidden>
                  {person.avatarEmoji || initials(person.displayName)}
                </span>
              )}
              <span className="people-who">
                <strong>{person.displayName}</strong>
                {nick ? <span className="people-nick">@{nick}</span> : null}
                <span className="meta">
                  {person.online ? t(lang, "online") : seen(person.lastSeen, lang)}
                  {staff && person.role ? ` · ${person.role}` : ""}
                  {staff && person.heronSub ? ` · ${person.heronSub}` : ""}
                  {staff && person.email ? ` · ${person.email}` : ""}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
